import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { hotelsAPI, activitiesAPI } from '../services/api';
import ErrorMessage from './ErrorMessage';
import LoadingSpinner from './LoadingSpinner';
import { useSelectedActivities } from './SelectedActivitiesContext';
import { useDate } from './DateContext';

const HotelDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [hotel, setHotel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedImage, setSelectedImage] = useState(0);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [modalImageIndex, setModalImageIndex] = useState(0);
  const [activities, setActivities] = useState([]);
  const { selectedActivities } = useSelectedActivities();
  const { checkIn, checkOut, formatDate } = useDate();

  useEffect(() => {
    fetchHotel();
    fetchActivities();
  }, [id]);

  // Enable sticky booking sidebar (overflow-x:clip only while this page is open)
  useEffect(() => {
    document.documentElement.classList.add('detail-page-sticky-enabled');
    return () => {
      document.documentElement.classList.remove('detail-page-sticky-enabled');
    };
  }, []);

  const fetchHotel = async () => {
    try {
      setError('');
      const response = await hotelsAPI.getById(id);
      setHotel(response.data);
    } catch (error) {
      console.error('Error fetching hotel:', error);
      setError('Unable to load hotel details. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchActivities = async () => {
    try {
      const response = await activitiesAPI.getAll();
      setActivities(response.data);
    } catch (e) {
      // Best-effort; if API fails, leave activities empty so summary hides
      setActivities([]);
    }
  };

  const openImageModal = (index) => {
    setModalImageIndex(index);
    setIsImageModalOpen(true);
  };

  const closeImageModal = () => {
    setIsImageModalOpen(false);
  };

  const nextImage = () => {
    if (hotel && hotel.images) {
      setModalImageIndex((prev) => (prev + 1) % hotel.images.length);
    }
  };

  const prevImage = () => {
    if (hotel && hotel.images) {
      setModalImageIndex((prev) => (prev - 1 + hotel.images.length) % hotel.images.length);
    }
  };

  // Handle keyboard navigation
  useEffect(() => {
    if (!isImageModalOpen || !hotel || !hotel.images) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        closeImageModal();
      } else if (e.key === 'ArrowRight') {
        setModalImageIndex((prev) => (prev + 1) % hotel.images.length);
      } else if (e.key === 'ArrowLeft') {
        setModalImageIndex((prev) => (prev - 1 + hotel.images.length) % hotel.images.length);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isImageModalOpen, hotel]);

  const handleBookHotel = () => {
    if (!hotel) {
      return;
    }

    const phoneNumber = '9459457733'; // WhatsApp number (945 945 7733)
    
    // Build message with hotel details
    let message = 'Hello, I would like to book the following property:\n\n';
    message += `🏨 Property: ${hotel.name}\n`;
    message += `📍 Location: ${hotel.location}\n`;
    if (checkIn && checkOut) {
      message += `📅 Check-in: ${formatDate(checkIn)}\n`;
      message += `📅 Check-out: ${formatDate(checkOut)}\n`;
    }
    if (hotel.locationDetail) {
      message += `   ${hotel.locationDetail}\n`;
    }
    if (hotel.category) {
      message += `🏷️ Category: ${hotel.category}\n`;
    }
    if (hotel.rating) {
      message += `⭐ Rating: ${hotel.rating}/5 (${hotel.ratingText || 'Good'})\n`;
    }
    message += `💰 Price: ₹${hotel.currentPrice.toLocaleString()} per night\n`;
    if (hotel.originalPrice && hotel.originalPrice !== hotel.currentPrice) {
      message += `   Original Price: ₹${hotel.originalPrice.toLocaleString()}\n`;
    }
    if (hotel.taxes > 0) {
      message += `   Taxes & Fees: ₹${hotel.taxes}\n`;
    }
    
    if (hotel.roomType) {
      message += `🛏️ Room Type: ${hotel.roomType}\n`;
    }
    if (hotel.mealPlan) {
      message += `🍽️ Meal Plan: ${hotel.mealPlan}\n`;
    }
    if (hotel.roomsAvailability) {
      message += `   Rooms Available: ${hotel.roomsAvailability}\n`;
    }
    
    if (hotel.amenities && hotel.amenities.length > 0) {
      message += `\n✅ Amenities:\n`;
      hotel.amenities.slice(0, 5).forEach((amenity, index) => {
        message += `${index + 1}. ${amenity}\n`;
      });
    }
    
    if (hotel.pool !== undefined) {
      message += `\n🏊 Swimming Pool: ${hotel.pool ? 'Available' : 'Not Available'}\n`;
    }
    if (hotel.restaurant !== undefined) {
      message += `🍽️ Restaurant: ${hotel.restaurant ? 'Available' : 'Not Available'}\n`;
    }
    
    if (hotel.contact) {
      message += `📞 Contact: ${hotel.contact}\n`;
    }

    // Add selected activities if any
    if (selectedActivities.length > 0 && activities.length > 0) {
      message += `\n\n🎯 Selected Activities (${selectedActivities.length}):\n\n`;
      let activitiesTotal = 0;
      selectedActivities.forEach((activityId, index) => {
        const activity = activities.find(a => String(a._id) === String(activityId));
        if (activity) {
          message += `${index + 1}. ${activity.activityName}\n`;
          if (activity.location) {
            message += `   Location: ${activity.location}\n`;
          }
          message += `   Price: ₹${activity.costing}\n`;
          if (activity.inclusion) {
            message += `   Inclusions: ${activity.inclusion}\n`;
          }
          message += '\n';
          activitiesTotal += activity.costing || 0;
        }
      });
      message += `Activities Total: ₹${activitiesTotal}\n`;
    }
    
    // Calculate grand total
    const hotelTotal = hotel.currentPrice + hotel.taxes;
    const activitiesTotal = selectedActivities.reduce((total, activityId) => {
      const activity = activities.find(a => String(a._id) === String(activityId));
      return total + (activity?.costing || 0);
    }, 0);
    const grandTotal = hotelTotal + activitiesTotal;
    
    message += `\n💰 Grand Total: ₹${grandTotal.toLocaleString()}\n`;
    message += `   (Property: ₹${hotelTotal.toLocaleString()} + Activities: ₹${activitiesTotal.toLocaleString()})\n`;
    
    message += '\n\nPlease confirm the booking. Thank you!';

    const encodedMessage = encodeURIComponent(message);
    const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodedMessage}`;
    window.open(whatsappUrl, '_blank');
  };

  const renderStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;

    for (let i = 0; i < fullStars; i++) {
      stars.push(<i key={i} className="fas fa-star" style={{color: '#FFD700'}}></i>);
    }

    if (hasHalfStar) {
      stars.push(<i key="half" className="fas fa-star-half-alt" style={{color: '#FFD700'}}></i>);
    }

    const emptyStars = 5 - Math.ceil(rating);
    for (let i = 0; i < emptyStars; i++) {
      stars.push(<i key={`empty-${i}`} className="far fa-star" style={{color: '#FFD700'}}></i>);
    }

    return stars;
  };

      if (loading) {
        return (
          <div className="hotel-detail-loading">
            <div className="container">
              <LoadingSpinner text="Loading hotel details..." />
            </div>
          </div>
        );
      }

  if (!hotel && !error) {
    return (
      <div className="hotel-detail-error">
        <div className="container">
          <div style={{ textAlign: 'center', padding: '50px' }}>
            <h2>Hotel not found</h2>
            <p>The hotel you're looking for doesn't exist or has been removed.</p>
            <button onClick={() => navigate('/home')} className="btn btn-primary">
              Back to Hotels
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="hotel-detail-error">
        <div className="container">
          <div style={{ textAlign: 'center', padding: '50px' }}>
            <ErrorMessage 
              message={error} 
              type="error" 
              onClose={() => setError('')}
            />
            <button onClick={() => navigate('/home')} className="btn btn-primary" style={{ marginTop: '20px' }}>
              Back to Hotels
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="hotel-detail-page has-hero">
      <Helmet>
        <title>{hotel.name} | Hotels in Goa | GoaDeals</title>
        <meta name="description" content={`${hotel.name} - ${hotel.location}. ₹${hotel.currentPrice?.toLocaleString?.() || hotel.currentPrice} per night. Book now on GoaDeals.`} />
        <meta property="og:title" content={`${hotel.name} | GoaDeals`} />
        <meta property="og:description" content={`${hotel.name}, ${hotel.location}. Book your stay in Goa.`} />
      </Helmet>

      <div className="container">
        {/* Back Button */}
        <div className="back-button">
          <button onClick={() => navigate('/home')} className="btn btn-secondary">
            ← Back to Hotels
          </button>
        </div>

        {/* Hotel Header */}
        <div className="hotel-detail-header">
          <h1>{hotel.name}</h1>
          <div className="hotel-location">
            <span className="location-name">{hotel.location}</span>
            <span className="location-detail">{hotel.locationDetail}</span>
            <div className="hotel-category">
              <span className="category-badge">{hotel.category}</span>
            </div>
          </div>
        </div>

        {/* Main Content Layout: Images Left, Booking Right */}
        <div className="hotel-detail-content-layout">
          {/* Left Side: Images and Details */}
          <div className="hotel-left-section">
            <div className="hotel-images-section">
              <div className="main-image-container" onClick={() => openImageModal(selectedImage)}>
                <img 
                  src={hotel.images[selectedImage]} 
                  alt={hotel.name}
                  className="hotel-main-image"
                  style={{ cursor: 'pointer' }}
                />
              </div>
              <div className="image-thumbnails">
                {hotel.images.map((image, index) => (
                  <img
                    key={index}
                    src={image}
                    alt={`${hotel.name} view ${index + 1}`}
                    className={`thumbnail ${selectedImage === index ? 'active' : ''}`}
                    onClick={() => {
                      setSelectedImage(index);
                      openImageModal(index);
                    }}
                    style={{ cursor: 'pointer' }}
                  />
                ))}
              </div>
            </div>

            {/* Facilities, Contact, and About Hotel Below Images */}
            <div className="hotel-info-below-images">
              <div className="hotel-facilities">
                <h3>Facilities & Contact</h3>
                <div className="facilities-grid">
                  <div className="facility-item">
                    <span className="facility-icon">🏊</span>
                    <span>{hotel.pool ? 'Swimming Pool Available' : 'No Swimming Pool'}</span>
                  </div>
                  <div className="facility-item">
                    <span className="facility-icon">🍽️</span>
                    <span>{hotel.restaurant ? 'Restaurant Available' : 'No Restaurant'}</span>
                  </div>
                  <div className="facility-item">
                    <span className="facility-icon">🛏️</span>
                    <span>{hotel.roomType} - {hotel.roomsAvailability} Rooms Available</span>
                  </div>
                  <div className="facility-item">
                    <span className="facility-icon">🍽️</span>
                    <span>Meal Plan: {hotel.mealPlan}</span>
                  </div>
                  <div className="facility-item">
                    <span className="facility-icon">📞</span>
                    <span>Contact: {hotel.contact}</span>
                  </div>
                  <div className="facility-item">
                    <span className="facility-icon">📍</span>
                    <a href={hotel.glink} target="_blank" rel="noopener noreferrer" className="map-link">
                      View on Google Maps
                    </a>
                  </div>
                </div>
              </div>

              <div className="hotel-description">
                <h3>About this hotel</h3>
                <p>{hotel.description}</p>
              </div>
            </div>
          </div>

          {/* Right Side: Booking Card & Selected Activities */}
          <div className="hotel-booking-section">
            <div className="booking-card">
              {/* Star Rating in Top Right Corner */}
              <div className="hotel-rating-top-right">
                <div className="stars">
                  {renderStars(hotel.rating)}
                </div>
                <span className="rating-text">{hotel.ratingText}</span>
                <span className="rating-count">({hotel.totalRatings} Ratings)</span>
              </div>

              {/* Tags and Amenities */}
              {hotel.tags && hotel.tags.length > 0 && (
                <div className="hotel-tags-booking">
                  {hotel.tags.map((tag, index) => (
                    <span key={index} className="tag">{tag}</span>
                  ))}
                </div>
              )}

              {hotel.amenities && hotel.amenities.length > 0 && (
                <div className="hotel-amenities-booking">
                  <h4>Amenities</h4>
                  <ul>
                    {hotel.amenities.slice(0, 5).map((amenity, index) => (
                      <li key={index}>
                        <span className="amenity-icon">✓</span>
                        {amenity}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="offer-badge">{hotel.offer}</div>
              <div className="price-details">
                <div className="price-row">
                  <span className="original-price">₹ {hotel.originalPrice.toLocaleString()}</span>
                  <span className="current-price">₹ {hotel.currentPrice.toLocaleString()}</span>
                </div>
                {hotel.taxes > 0 && (
                  <div className="taxes">+ ₹ {hotel.taxes} taxes & fees</div>
                )}
                <div className="per-night">Per Night</div>
              </div>
              
              <div className="booking-actions">
                <button 
                  className="btn btn-primary btn-large"
                  onClick={handleBookHotel}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    backgroundColor: '#809947',
                    border: 'none',
                    width: '100%'
                  }}
                >
                  <i className="fab fa-whatsapp" style={{ fontSize: '1.2rem' }}></i>
                  Book Now
                </button>
              </div>

              {selectedActivities.length > 0 && activities.length > 0 && (
                <div className="selected-activities-summary" style={{ marginTop: '20px', paddingTop: '20px', borderTop: '1px solid #e9ecef' }}>
                  <h4 style={{ fontSize: '1rem', marginBottom: '12px', color: '#428cb1' }}>Selected Activities ({selectedActivities.length})</h4>
                  <div className="selected-list" style={{ marginBottom: '12px' }}>
                    {selectedActivities.map(activityId => {
                      const activity = activities.find(a => String(a._id) === String(activityId));
                      if (!activity) return null;
                      return (
                        <div key={activityId} className="selected-activity" style={{ padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>
                          <span style={{ fontSize: '0.9rem' }}>{activity.activityName}</span>
                          <span className="selected-price" style={{ fontSize: '0.9rem', fontWeight: '600', color: '#809947' }}>₹{activity.costing}</span>
                        </div>
                      );
                    })}
                  </div>
                  <div className="total-price" style={{ paddingTop: '12px', borderTop: '2px solid #428cb1', textAlign: 'center' }}>
                    <strong style={{ fontSize: '1.1rem', color: '#428cb1' }}>
                      Activities Total: ₹{selectedActivities.reduce((total, activityId) => {
                        const activity = activities.find(a => String(a._id) === String(activityId));
                        return total + (activity?.costing || 0);
                      }, 0)}
                    </strong>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Fixed bottom booking bar (visible on tablet/mobile when content is long) */}
      {hotel && (
        <div className="booking-bar-fixed" aria-hidden="true">
          <button
            type="button"
            className="btn-book-now-fixed"
            onClick={handleBookHotel}
          >
            <i className="fab fa-whatsapp" style={{ fontSize: '1.2rem' }}></i>
            Book Now
          </button>
        </div>
      )}

      {/* Image Modal/Popover */}
      {isImageModalOpen && hotel && hotel.images && (
        <div className="image-modal-overlay" onClick={closeImageModal}>
          <div className="image-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="image-modal-close" onClick={closeImageModal}>
              <i className="fas fa-times"></i>
            </button>
            <button className="image-modal-prev" onClick={prevImage}>
              <i className="fas fa-chevron-left"></i>
            </button>
            <div className="image-modal-image-container">
              <img 
                src={hotel.images[modalImageIndex]} 
                alt={`${hotel.name} - Image ${modalImageIndex + 1}`}
                className="image-modal-image"
              />
            </div>
            <button className="image-modal-next" onClick={nextImage}>
              <i className="fas fa-chevron-right"></i>
            </button>
            <div className="image-modal-counter">
              {modalImageIndex + 1} / {hotel.images.length}
            </div>
            <div className="image-modal-thumbnails">
              {hotel.images.map((image, index) => (
                <img
                  key={index}
                  src={image}
                  alt={`Thumbnail ${index + 1}`}
                  className={`image-modal-thumbnail ${modalImageIndex === index ? 'active' : ''}`}
                  onClick={() => setModalImageIndex(index)}
                />
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HotelDetail;


